import log from "loglevel";
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";

import { SUPPORT_CHAT_WS_URL } from "api-config.mjs";
import { extractAuthHeaders } from "api.mjs";
import { isBrowser } from "components/Browser";
import { useSessionContext } from "components/Session";

// App-level state for the support chat.
//
// Every page renders its own <Layout>, and Layout mounts the widget — so a
// Gatsby page change unmounts one widget and mounts a fresh one. The
// conversation must therefore live ABOVE the pages: this provider is mounted
// once from containers/App.js (Gatsby's wrapRootElement), which stays mounted
// across client-side navigation. It owns the thread, the conversation_id, the
// draft in the text box AND the WebSocket, so a reply that is still in flight
// when the visitor clicks a link lands in the chat on the next page.
//
// The thread is also mirrored to sessionStorage, so a full reload (or a link
// that isn't a Gatsby <Link>) restores it. sessionStorage, not localStorage,
// on purpose: it is per tab and dies with the tab, so a shared computer never
// shows one visitor's questions to the next, and a week-old conversation never
// reappears. Whether the panel is open is NOT persisted — a reload starts
// closed, the way the rest of the site does.
//
// This module is imported statically by App, so it must stay light: the
// markdown renderer and everything else UI lives in SupportChatWidget, which
// Layout loads lazily.

const RESPONSE_TIMEOUT_SECONDS = 120;
const DEVICE_ID_STORAGE_KEY = "supportChatDeviceId";
const CONVERSATION_STORAGE_KEY = "supportChatConversation";
// Bump when the stored shape changes; an older version is discarded rather
// than half-read.
const CONVERSATION_STORAGE_VERSION = 1;

export const WELCOME_MESSAGE = {
  role: "assistant",
  text:
    "Hi! I can answer questions about EarningsCall — coverage, plans and " +
    "pricing, and how to use the API. What can I help with?",
  badges: [],
};

// Shown before the visitor has typed anything, so the first message is one tap.
// Static on purpose: no backend call, no latency. After each answer the backend
// replaces these with contextual quick replies (a "suggested_replies" chunk).
export const STARTER_SUGGESTIONS = [
  "How do I authenticate my API requests?",
  "Which plan is right for me?",
  "I have a billing issue",
];

// A pending bubble restored from storage can never resolve: the request_id it
// was waiting on died with the previous page load.
const RELOAD_INTERRUPTED_ERROR =
  "This reply was interrupted by a page reload — please ask again.";

// error_type values the backend sends (cca/support/worker.py) when the
// conversation_id we hold can no longer be continued: it was never stored, it
// has since been deleted, or it belongs to a different device id. The visible
// thread stays as it is; the next message simply opens a new conversation on
// the server instead of failing forever.
const STALE_CONVERSATION_ERROR_TYPES = new Set([
  "unknown_conversation",
  "conversation_forbidden",
]);

const isStoredMessage = (message) =>
  message &&
  typeof message === "object" &&
  (message.role === "user" || message.role === "assistant") &&
  typeof message.text === "string" &&
  Array.isArray(message.badges);

const freshConversation = () => ({
  messages: [WELCOME_MESSAGE],
  conversationId: null,
  suggestions: STARTER_SUGGESTIONS,
});

const loadStoredConversation = () => {
  if (!isBrowser()) {
    return freshConversation();
  }
  try {
    const stored = JSON.parse(sessionStorage.getItem(CONVERSATION_STORAGE_KEY));
    if (
      !stored ||
      stored.version !== CONVERSATION_STORAGE_VERSION ||
      !Array.isArray(stored.messages) ||
      stored.messages.length === 0 ||
      !stored.messages.every(isStoredMessage)
    ) {
      return freshConversation();
    }
    return {
      messages: stored.messages.map((message) =>
        message.pending
          ? { ...message, pending: false, error: RELOAD_INTERRUPTED_ERROR }
          : message
      ),
      conversationId:
        typeof stored.conversationId === "string" ? stored.conversationId : null,
      suggestions: Array.isArray(stored.suggestions)
        ? stored.suggestions.filter((s) => typeof s === "string")
        : [],
    };
  } catch (error) {
    // Corrupt stored value — start over.
    return freshConversation();
  }
};

// Stable anonymous device id: the backend's per-device daily quota keys on it.
const getDeviceId = () => {
  if (!isBrowser()) {
    return null;
  }
  let deviceId = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
  if (!deviceId) {
    deviceId = crypto.randomUUID();
    localStorage.setItem(DEVICE_ID_STORAGE_KEY, deviceId);
  }
  return deviceId;
};

const SupportChatContext = createContext(null);

export const useSupportChat = () => {
  const value = useContext(SupportChatContext);
  if (value === null) {
    throw new Error(
      "useSupportChat must be used inside <SupportChatProvider> (mounted in containers/App.js)"
    );
  }
  return value;
};

export const SupportChatProvider = ({ children }) => {
  const [initial] = useState(loadStoredConversation);
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState(initial.messages);
  const [inputValue, setInputValue] = useState("");
  const [isWaiting, setIsWaiting] = useState(false);
  const [conversationId, setConversationId] = useState(initial.conversationId);
  const [quota, setQuota] = useState(null);
  const [suggestions, setSuggestions] = useState(initial.suggestions);
  const { sessionContext } = useSessionContext();
  const timeoutRef = useRef(null);
  const didUnmount = useRef(false);
  // request_id of the in-flight query. Server chunks echo the request_id they
  // answer; anything else (e.g. a reply arriving after the 120s timeout, once
  // the user has already sent a new message) is stale and must be ignored, or
  // it would resolve the wrong bubble and overwrite conversation_id/quota.
  const pendingRequestIdRef = useRef(null);
  // The in-flight query's text, kept so it can be re-sent on a fresh
  // conversation if the server no longer knows the stored conversation_id.
  const pendingQueryRef = useRef(null);
  // Set once the in-flight query has been re-sent on a fresh conversation, so
  // a second stale-conversation error cannot loop.
  const restartedConversationRef = useRef(false);
  // request_id of the most recently COMPLETED query. The suggested_replies
  // chunk arrives after query_response has already cleared pendingRequestIdRef,
  // so it needs its own staleness gate: chips must belong to the answer the
  // visitor is looking at, never to an earlier turn.
  const lastCompletedRequestIdRef = useRef(null);
  // Set by openPanel and consumed (once) by the widget, which moves focus into
  // the dialog. A ref rather than an effect on isOpen in the widget: the
  // widget remounts on every page change while the panel stays open, and an
  // isOpen effect would grab focus — and pop the phone keyboard — on each one.
  const focusRequestRef = useRef(false);

  useEffect(() => {
    return () => {
      didUnmount.current = true;
      clearResponseTimeout();
    };
  }, []);

  // Mirror the thread to sessionStorage (see the header comment). quota is
  // deliberately left out: it is a daily allowance, and a stored
  // "0 remaining" would keep the box disabled past the reset — the server
  // re-reports it with every answer anyway.
  useEffect(() => {
    if (!isBrowser()) {
      return;
    }
    try {
      sessionStorage.setItem(
        CONVERSATION_STORAGE_KEY,
        JSON.stringify({
          version: CONVERSATION_STORAGE_VERSION,
          messages,
          conversationId,
          suggestions,
        })
      );
    } catch (error) {
      log.warn("support-chat: could not persist the conversation", error);
    }
  }, [messages, conversationId, suggestions]);

  // Connect while the panel is open (third arg) and reconnect with backoff
  // like the investor chat (ChatContainer.js). Also stay connected while a
  // reply is pending, so closing the panel mid-answer doesn't lose it.
  const { sendMessage, lastMessage, readyState } = useWebSocket(
    SUPPORT_CHAT_WS_URL,
    {
      shouldReconnect: () => didUnmount.current === false,
      reconnectAttempts: 10,
      reconnectInterval: (attemptNumber) =>
        Math.min(Math.pow(2, attemptNumber) * 1_000, 10_000),
    },
    isOpen || isWaiting
  );

  const clearResponseTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const failPendingMessage = (errorText) => {
    pendingRequestIdRef.current = null;
    pendingQueryRef.current = null;
    setMessages((prev) => {
      const next = [...prev];
      const last = next[next.length - 1];
      if (last?.pending) {
        next[next.length - 1] = { ...last, pending: false, error: errorText };
      }
      return next;
    });
    setIsWaiting(false);
  };

  const startResponseTimeout = () => {
    clearResponseTimeout();
    timeoutRef.current = setTimeout(() => {
      log.error("support-chat: server did not respond in time");
      failPendingMessage("The server did not respond in time. Please try again.");
    }, RESPONSE_TIMEOUT_SECONDS * 1_000);
  };

  // Put a query on the wire. Everything visible (bubbles, chips, the draft)
  // is sendQuery's job — this is also what re-sends a query on a fresh
  // conversation, where nothing visible should change.
  const transmit = (query, threadId) => {
    const headers = extractAuthHeaders({ context: { sessionContext } });
    const requestId = crypto.randomUUID();
    pendingRequestIdRef.current = requestId;
    pendingQueryRef.current = query;
    const payload = {
      query,
      conversation_id: threadId,
      action_handler: "support",
      headers,
      device_id: getDeviceId(),
      request_id: requestId,
    };
    // headers carries the visitor's real session token, and alpha logs at
    // debug on a publicly reachable deployment — never print it (#684 review).
    log.debug("support-chat: sending", { ...payload, headers: "[redacted]" });
    sendMessage(JSON.stringify(payload));
    startResponseTimeout();
  };

  const handleServerMessage = (message) => {
    let parsed;
    try {
      parsed = JSON.parse(message.data);
    } catch (error) {
      log.error("support-chat: unparseable message", message.data);
      return;
    }
    log.debug("support-chat: received", parsed);
    const { status, message_type: messageType } = parsed;
    if (status === "error") {
      // Mirror of the stale-chunk gate below (#684 review): error chunks that
      // can straggle in after the 120s timeout — invalid query, unknown
      // conversation, quota_exceeded (cca/support/worker.py) — echo the
      // request_id they answer, and a stale one must not fail the NEW
      // message's live bubble. Errors WITHOUT a request_id (invalid JSON,
      // auth, internal error) always answer the live request; apply as before.
      if (
        parsed.request_id !== undefined &&
        parsed.request_id !== pendingRequestIdRef.current
      ) {
        log.debug(
          "support-chat: dropping stale error chunk for request",
          parsed.request_id
        );
        return;
      }
      if (
        STALE_CONVERSATION_ERROR_TYPES.has(parsed.error_type) &&
        conversationId !== null &&
        pendingQueryRef.current !== null &&
        !restartedConversationRef.current
      ) {
        // The stored conversation is gone on the server (a restored thread
        // whose conversation was deleted, or a device id that no longer
        // matches). Keep the visible thread, drop the id, and re-send the
        // same question as the first message of a new conversation. The
        // server refuses before consuming quota, so the retry costs one turn.
        log.warn(
          "support-chat: server no longer knows conversation",
          conversationId,
          "— starting a new one"
        );
        restartedConversationRef.current = true;
        setConversationId(null);
        transmit(pendingQueryRef.current, null);
        return;
      }
      clearResponseTimeout();
      failPendingMessage(
        parsed.message || "Something went wrong. Please try again."
      );
      return;
    }
    if (status !== "success") {
      return;
    }
    if (messageType === "suggested_replies") {
      // Arrives after query_response, when pendingRequestIdRef is already null,
      // so the in-flight gate below would wrongly drop it. Its own gate: the
      // chunk must belong to the query that just completed, and no new query
      // may be in flight (late chips must not attach to the wrong answer).
      if (
        pendingRequestIdRef.current !== null ||
        parsed.request_id !== lastCompletedRequestIdRef.current
      ) {
        log.debug(
          "support-chat: dropping stale suggested_replies chunk",
          parsed.request_id
        );
        return;
      }
      setSuggestions(parsed.result.suggested_replies);
      return;
    }
    // Success chunks carry the request_id of the query they answer (error
    // chunks don't — the worker's error paths omit it). Drop anything that
    // isn't answering the current in-flight request.
    if (parsed.request_id !== pendingRequestIdRef.current) {
      log.debug(
        "support-chat: dropping stale chunk for request",
        parsed.request_id
      );
      return;
    }
    if (messageType === "tool_calls") {
      startResponseTimeout();
      // Surface progress as a status badge on the pending assistant message,
      // e.g. "Checking coverage...", "Searching the docs...".
      const badge = parsed.agent_message;
      setMessages((prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        if (last?.pending && badge && !last.badges.includes(badge)) {
          next[next.length - 1] = { ...last, badges: [...last.badges, badge] };
        }
        return next;
      });
    } else if (messageType === "tool_call_response") {
      startResponseTimeout();
    } else if (messageType === "query_response") {
      clearResponseTimeout();
      lastCompletedRequestIdRef.current = pendingRequestIdRef.current;
      pendingRequestIdRef.current = null;
      pendingQueryRef.current = null;
      const { result } = parsed;
      setConversationId(result.conversation_id);
      setQuota(result.quota);
      setMessages((prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        next[next.length - 1] = {
          ...last,
          pending: false,
          text: result.query.answer,
        };
        return next;
      });
      setIsWaiting(false);
    }
  };

  useEffect(() => {
    if (lastMessage !== null) {
      handleServerMessage(lastMessage);
    }
  }, [lastMessage]);

  const quotaExhausted = Boolean(quota && quota.remaining_daily_queries <= 0);

  // Shared by the input form and the suggestion chips — a chip click is exactly
  // a pre-typed send, so every guard applies to both paths. Returns whether a
  // message actually went out.
  const sendQuery = (text) => {
    const query = text.trim();
    if (!query || isWaiting || quotaExhausted) {
      return false;
    }
    if (readyState !== ReadyState.OPEN) {
      log.error("support-chat: websocket not open, state:", readyState);
      return false;
    }
    restartedConversationRef.current = false;
    transmit(query, conversationId);
    setMessages((prev) => [
      ...prev,
      { role: "user", text: query, badges: [] },
      { role: "assistant", text: "", badges: [], pending: true },
    ]);
    // Chips answer the previous turn; once a new query is out they're stale.
    // Fresh ones arrive with the next answer's suggested_replies chunk.
    setSuggestions([]);
    setInputValue("");
    setIsWaiting(true);
    return true;
  };

  const openPanel = () => {
    focusRequestRef.current = true;
    setIsOpen(true);
  };
  const closePanel = () => setIsOpen(false);
  const togglePanel = () => (isOpen ? closePanel() : openPanel());
  const takeFocusRequest = () => {
    const requested = focusRequestRef.current;
    focusRequestRef.current = false;
    return requested;
  };

  return (
    <SupportChatContext.Provider
      value={{
        isOpen,
        openPanel,
        closePanel,
        togglePanel,
        takeFocusRequest,
        messages,
        suggestions,
        isWaiting,
        quotaExhausted,
        readyState,
        inputValue,
        setInputValue,
        sendQuery,
      }}
    >
      {children}
    </SupportChatContext.Provider>
  );
};

export default SupportChatProvider;
